/**
 * DeleteConfirmDialog — reusable enterprise delete dialog.
 *
 * Usage:
 *   const { confirm } = useDeleteConfirm();
 *   const ok = await confirm({ api, module: 'customers', id, name: 'John Doe' });
 *   if (ok) reloadList();
 *
 * Features:
 *   • Pre-flight dependency check (counts child records)
 *   • Optional "delete reason" textarea
 *   • Optional "cascade delete related records" checkbox (when deps exist)
 *   • Undo toast (15s) after successful delete via sonner
 *   • Type-to-confirm "DELETE" for permanent delete
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const Ctx = createContext(null);

const PRETTY = {
  bookings: 'Bookings', invoices: 'Invoices', payments: 'Payments',
  warranties: 'Warranties', amc_contracts: 'AMC Contracts',
  quotations: 'Quotations', service_tickets: 'Service Tickets',
};

export function DeleteConfirmProvider({ children, api }) {
  const [state, setState] = useState(null); // null | { module, id, name, mode, onResolve }
  const value = useMemo(() => ({
    confirm: (opts) => new Promise((resolve) => {
      setState({ ...opts, onResolve: resolve, mode: opts.mode || 'soft' });
    }),
  }), []);

  return (
    <Ctx.Provider value={value}>
      {children}
      {state && <DeleteDialog api={api} {...state} onClose={(result) => { state.onResolve(result); setState(null); }} />}
    </Ctx.Provider>
  );
}

export function useDeleteConfirm() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDeleteConfirm must be used inside <DeleteConfirmProvider>');
  return ctx;
}

function DeleteDialog({ api, module, id, name, mode, onClose }) {
  const [deps, setDeps] = useState(null);
  const [loadingDeps, setLoadingDeps] = useState(true);
  const [reason, setReason] = useState('');
  const [cascade, setCascade] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const isPermanent = mode === 'permanent';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api('GET', `/admin/recycle-bin/${module}/${id}/dependencies`);
        if (!cancelled) setDeps(r.data);
      } catch (e) {
        if (!cancelled) setDeps({ dependencies: {}, dependency_total: 0, record: { id } });
      } finally {
        if (!cancelled) setLoadingDeps(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, module, id]);

  const onEsc = useCallback((e) => { if (e.key === 'Escape' && !busy) onClose(false); }, [busy, onClose]);
  useEffect(() => { window.addEventListener('keydown', onEsc); return () => window.removeEventListener('keydown', onEsc); }, [onEsc]);

  const doDelete = async () => {
    if (isPermanent && typed.trim() !== 'DELETE') return;
    setBusy(true);
    try {
      if (isPermanent) {
        await api('POST', '/admin/recycle-bin/bulk', { action: 'permanent_delete', module, ids: [id], confirm: 'DELETE', reason });
        toast.success(`${name || id} permanently deleted`);
      } else {
        // Use ?reason=&cascade= query params via direct axios (api helper sends body for non-GET)
        const params = new URLSearchParams();
        if (reason.trim()) params.set('reason', reason.trim());
        if (cascade) params.set('cascade', 'true');
        await api('DELETE', `/admin/recycle-bin/${module}/${id}?${params.toString()}`);

        // Undo snackbar
        toast(`${name || id} moved to Recycle Bin`, {
          duration: 15000,
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                await api('POST', `/admin/recycle-bin/${module}/${id}/restore`);
                toast.success('Restored');
              } catch (e) {
                toast.error('Restore failed: ' + (e?.response?.data?.detail || e.message));
              }
            },
          },
        });
      }
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const depTotal = deps?.dependency_total || 0;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(false); }} data-testid="delete-confirm-dialog">
      <div className="bg-white w-full sm:max-w-md rounded-2xl overflow-hidden shadow-2xl">
        <div className={`flex items-start gap-3 px-5 py-4 ${isPermanent ? 'bg-red-600' : 'bg-amber-500'} text-white`}>
          <AlertTriangle size={22} className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-base">{isPermanent ? 'Permanently delete?' : 'Move to Recycle Bin?'}</div>
            <div className="text-xs opacity-90 mt-0.5">{isPermanent ? 'This cannot be undone.' : 'You can restore from the Recycle Bin within 30 days.'}</div>
          </div>
          <button type="button" onClick={() => !busy && onClose(false)} className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="del-dlg-close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="bg-slate-50 rounded-lg p-3 text-sm border border-slate-200">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{module}</div>
            <div className="font-semibold text-slate-900">{name || deps?.record?.name || deps?.record?.customer_name || deps?.record?.code || deps?.record?.invoice_number || id}</div>
            <div className="text-[11px] text-slate-500 mt-0.5 font-mono truncate">{id}</div>
          </div>

          {loadingDeps ? (
            <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Checking related records…</div>
          ) : depTotal > 0 ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <div className="font-bold mb-1">This record has {depTotal} related items:</div>
              <ul className="list-disc ml-5 space-y-0.5">
                {Object.entries(deps.dependencies).map(([coll, n]) => (
                  <li key={coll}>{n} {PRETTY[coll] || coll}</li>
                ))}
              </ul>
              {deps.cascade_supported && !isPermanent && (
                <label className="flex items-center gap-2 mt-2 pt-2 border-t border-amber-200 cursor-pointer">
                  <input type="checkbox" checked={cascade} onChange={(e) => setCascade(e.target.checked)} data-testid="del-dlg-cascade" />
                  <span className="font-semibold">Also delete the related records above</span>
                </label>
              )}
            </div>
          ) : null}

          <div>
            <Label className="text-xs font-semibold text-slate-700">Reason (optional)</Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="e.g. Duplicate entry, customer request"
              className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
              data-testid="del-dlg-reason"
            />
          </div>

          {isPermanent && (
            <div>
              <Label className="text-xs font-semibold text-red-700">Type <code className="bg-red-50 px-1 rounded">DELETE</code> to confirm</Label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" className="mt-1" autoComplete="off" data-testid="del-dlg-type-confirm" />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex gap-2 justify-end bg-slate-50">
          <Button variant="outline" onClick={() => onClose(false)} disabled={busy} data-testid="del-dlg-cancel">Cancel</Button>
          <Button
            onClick={doDelete}
            disabled={busy || loadingDeps || (isPermanent && typed.trim() !== 'DELETE')}
            className={isPermanent ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}
            data-testid="del-dlg-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Trash2 size={14} className="mr-1.5" />}
            {isPermanent ? 'Permanently Delete' : 'Move to Recycle Bin'}
          </Button>
        </div>
      </div>
    </div>
  );
}
